import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Play,
  CheckCircle,
  XCircle,
  Clock,
  TrendingUp,
  TrendingDown,
  BarChart3,
  ListChecks,
  AlertTriangle,
  Plus,
  ArrowRight,
  RefreshCw,
  Settings
} from 'lucide-react';
import { API_BASE_URL } from '../config';
import LoadingSpinner from '../components/LoadingSpinner';
import clsx from 'clsx';
import toast from 'react-hot-toast';

const QADashboardPage = () => {
  const [summary, setSummary] = useState(null);
  const [trends, setTrends] = useState([]);
  const [failures, setFailures] = useState([]);
  const [suites, setSuites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [runningTest, setRunningTest] = useState(null);
  const navigate = useNavigate();

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const [summaryRes, trendsRes, failuresRes, suitesRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/qa/dashboard/summary`, { headers }),
        fetch(`${API_BASE_URL}/api/qa/dashboard/trends?days=14`, { headers }),
        fetch(`${API_BASE_URL}/api/qa/dashboard/failures?limit=5`, { headers }),
        fetch(`${API_BASE_URL}/api/qa/suites`, { headers })
      ]);

      if (summaryRes.ok) {
        const data = await summaryRes.json();
        setSummary(data.summary);
      }
      if (trendsRes.ok) {
        const data = await trendsRes.json();
        setTrends(data.trends || []);
      }
      if (failuresRes.ok) {
        const data = await failuresRes.json();
        setFailures(data.failures || []);
      }
      if (suitesRes.ok) {
        const data = await suitesRes.json();
        setSuites(data.suites || []);
      }
    } catch (error) {
      console.error('Failed to fetch QA data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const runTestSuite = async (suiteId) => {
    try {
      setRunningTest(suiteId);
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/api/qa/suites/${suiteId}/run`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        navigate(`/app/qa/runs/${data.testRun.id}`);
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to start test run');
      }
    } catch (error) {
      console.error('Failed to start test:', error);
      toast.error('Failed to start test run');
    } finally {
      setRunningTest(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBg = (score) => {
    if (score >= 80) return 'bg-green-100';
    if (score >= 60) return 'bg-yellow-100';
    return 'bg-red-100';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">QA Testing Dashboard</h1>
          <p className="text-gray-600 mt-1">
            Automated testing and quality assurance for your voice AI
          </p>
        </div>
        <div className="flex space-x-3">
          <Link
            to="/app/qa/config"
            className="flex items-center px-3 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            <Settings className="w-4 h-4 mr-2" />
            Configure
          </Link>
          <button
            onClick={fetchData}
            className="flex items-center px-3 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </button>
          <Link
            to="/app/qa/suites/new"
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Test Suite
          </Link>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Test Suites</p>
              <p className="text-2xl font-bold text-gray-900">{summary?.totalSuites || 0}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-full">
              <ListChecks className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Total Runs</p>
              <p className="text-2xl font-bold text-gray-900">{summary?.totalRuns || 0}</p>
            </div>
            <div className="p-3 bg-purple-100 rounded-full">
              <BarChart3 className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Average Score</p>
              <p className={clsx('text-2xl font-bold', getScoreColor(summary?.averageScore || 0))}>
                {summary?.averageScore || '--'}%
              </p>
            </div>
            <div className={clsx('p-3 rounded-full', getScoreBg(summary?.averageScore || 0))}>
              <TrendingUp className={clsx('w-6 h-6', getScoreColor(summary?.averageScore || 0))} />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Pass Rate</p>
              <p className={clsx('text-2xl font-bold', getScoreColor(summary?.passRate || 0))}>
                {summary?.passRate || '--'}%
              </p>
            </div>
            <div className={clsx('p-3 rounded-full', getScoreBg(summary?.passRate || 0))}>
              <CheckCircle className={clsx('w-6 h-6', getScoreColor(summary?.passRate || 0))} />
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Test Suites */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900">Test Suites</h2>
            <Link to="/app/qa/suites" className="text-blue-600 hover:text-blue-700 text-sm flex items-center">
              View All <ArrowRight className="w-4 h-4 ml-1" />
            </Link>
          </div>
          <div className="divide-y divide-gray-200">
            {suites.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                <ListChecks className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No test suites yet</p>
                <Link to="/app/qa/suites/new" className="text-blue-600 hover:underline mt-2 inline-block">
                  Create your first test suite
                </Link>
              </div>
            ) : (
              suites.slice(0, 5).map((suite) => (
                <div key={suite.id} className="p-4 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium text-gray-900">{suite.name}</h3>
                      <p className="text-sm text-gray-500">
                        {suite.scenarios?.length || 0} scenarios | {suite._count?.runs || 0} runs
                      </p>
                    </div>
                    <button
                      onClick={() => runTestSuite(suite.id)}
                      disabled={runningTest === suite.id}
                      className={clsx(
                        'flex items-center px-3 py-1.5 rounded-lg text-sm font-medium',
                        runningTest === suite.id
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-green-100 text-green-700 hover:bg-green-200'
                      )}
                    >
                      {runningTest === suite.id ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                          Running...
                        </>
                      ) : (
                        <>
                          <Play className="w-4 h-4 mr-1" />
                          Run
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Recent Failures */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900">Recent Failures</h2>
            <span className="text-sm text-gray-500">{failures.length} issues</span>
          </div>
          <div className="divide-y divide-gray-200">
            {failures.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-300" />
                <p>No recent failures</p>
                <p className="text-sm mt-1">All tests are passing!</p>
              </div>
            ) : (
              failures.map((failure) => (
                <div key={failure.id} className="p-4 hover:bg-gray-50">
                  <div className="flex items-start space-x-3">
                    <div className="p-1.5 bg-red-100 rounded-full mt-0.5">
                      <XCircle className="w-4 h-4 text-red-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">
                        {failure.testScenario?.name || 'Unknown Scenario'}
                      </p>
                      <p className="text-sm text-gray-500 truncate">
                        {failure.failureReason || 'No reason provided'}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        {new Date(failure.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                    <Link
                      to={`/app/qa/executions/${failure.id}`}
                      className="text-blue-600 hover:text-blue-700 text-sm"
                    >
                      View
                    </Link>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Trends Chart */}
      {trends.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Score Trends (Last 14 Days)</h2>
          <div className="h-48 flex items-end space-x-2">
            {trends.map((day, index) => (
              <div key={day.date} className="flex-1 flex flex-col items-center">
                <div
                  className={clsx(
                    'w-full rounded-t transition-all',
                    day.averageScore >= 80 ? 'bg-green-500' :
                    day.averageScore >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                  )}
                  style={{ height: `${(day.averageScore || 0) * 1.5}px` }}
                  title={`${day.date}: ${day.averageScore}%`}
                />
                <span className="text-xs text-gray-500 mt-2 transform -rotate-45">
                  {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Runs */}
      {summary?.recentRuns?.length > 0 && (
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Recent Test Runs</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Score</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Passed</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Failed</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {summary.recentRuns.map((run) => (
                  <tr key={run.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {new Date(run.createdAt).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={clsx(
                        'px-2 py-1 text-sm font-medium rounded-full',
                        getScoreBg(Number(run.overallScore)),
                        getScoreColor(Number(run.overallScore))
                      )}>
                        {run.overallScore || '--'}%
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">
                      {run.passedScenarios}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600">
                      {run.failedScenarios}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <Link
                        to={`/app/qa/runs/${run.id}`}
                        className="text-blue-600 hover:text-blue-700"
                      >
                        View Details
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default QADashboardPage;
